/*
  Bedarfszeile im Shiftplan-Raster: Soll/Ist pro Tag, aufklappbar.
  Offene Bedarfe werden auf Mitarbeitende gezogen (einplanen);
  geplante Schichten können zurück auf die Bedarfszeile gezogen werden (entfernen).

  Die Zeile klebt unter dem Header und darf den Plan darunter nicht verdecken:
  ab vielen Bedarfen pro Tag werden gedeckte Chips zu einem Pill gebündelt,
  zusätzlich deckelt die CSS-Variable --spv2-demand-stack-max die Höhe.
*/

import React, { useEffect, useState } from "react";
import { Box, Typography } from "@mui/material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import { IconCheck, IconChevronDown } from "../../../../design-system/icons";
import ShiftplanTooltip from "../tooltips/ShiftplanTooltip";
import { buildDemandDragEntry } from "../../domain/model/demand";

const EMPTY_DAY = Object.freeze({
  items: [],
  requiredTotal: 0,
  plannedTotal: 0,
  missingTotal: 0,
  overTotal: 0,
  tone: "none",
});

// Erst ab mehr als so vielen Chips am vollsten Tag lohnt sich das Bündeln.
// Kleine Betriebe sehen ausgeklappt weiterhin jeden Bedarf.
const COVERED_FILTER_MIN_ITEMS = 4;

function formatMissingLabel(count) {
  return count === 1 ? "1 Schicht offen" : `${count} Schichten offen`;
}

function formatRowMeta(totalMissing, totalOver) {
  const parts = [];
  if (totalMissing > 0) parts.push(`${totalMissing} offen`);
  if (totalOver > 0) parts.push(`${totalOver} überplant`);
  return parts.length ? parts.join(" · ") : "Gedeckt";
}

function formatCoveredLabel(count) {
  return count === 1 ? "1 gedeckt" : `${count} gedeckt`;
}

function needsAttention(item) {
  return item.missingWorkers > 0 || item.overWorkers > 0;
}

function demandChipState(item) {
  if (item.missingWorkers > 0) return "open";
  if (item.overWorkers > 0) return "over";
  return "covered";
}

function chipTitleLabel(item) {
  return item.shiftShortName || item.shiftName || item.departmentName || "Schicht";
}

function buildChipTooltip(item) {
  const parts = [
    [item.shiftName, item.departmentName].filter(Boolean).join(" · "),
    item.timeLabel,
  ].filter(Boolean);

  if (item.overWorkers > 0) {
    parts.push(`${item.overWorkers} überplant`);
  }

  return parts.join(" · ");
}

function DaySummaryBadge({ day }) {
  if (day.missingTotal > 0 || day.overTotal > 0) {
    // Beide Zustände nebeneinander: ein Tag kann gleichzeitig offene und
    // überplante Schichten haben, und beides will man eingeklappt sehen.
    return (
      <Box component="span" className="spv2-demand-badges">
        {day.missingTotal > 0 ? (
          <ShiftplanTooltip title={formatMissingLabel(day.missingTotal)}>
            <Box component="span" className="spv2-demand-badge is-missing">-{day.missingTotal}</Box>
          </ShiftplanTooltip>
        ) : null}
        {day.overTotal > 0 ? (
          <ShiftplanTooltip title={`${day.overTotal} überplant`}>
            <Box component="span" className="spv2-demand-badge is-over">+{day.overTotal}</Box>
          </ShiftplanTooltip>
        ) : null}
      </Box>
    );
  }

  if (day.tone === "covered") {
    return (
      <ShiftplanTooltip title="Bedarf gedeckt">
        <Box component="span" className="spv2-demand-badge is-covered">
          <IconCheck fontSize="inherit" />
        </Box>
      </ShiftplanTooltip>
    );
  }

  return <Box component="span" className="spv2-demand-badge is-none" aria-hidden="true" />;
}

function DemandChip({ item, compact, canDrag, dragging, onDragStart, onDragEnd }) {
  const state = demandChipState(item);
  const isOpen = state === "open";
  const isDraggable = canDrag && isOpen;
  const chipColor = item.color || "var(--ds-color-text-secondary)";

  const handleDragStart = (event) => {
    event.stopPropagation();
    if (event.dataTransfer) {
      // Muss zum dropEffect "move" der Grid-Zellen passen, sonst verweigert
      // der Browser den Drop trotz sichtbarem Drop-Target.
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData("text/plain", String(item.id || ""));
    }
    onDragStart?.(item);
  };

  const handleDragEnd = (event) => {
    event.stopPropagation();
    onDragEnd?.(item);
  };

  return (
    <ShiftplanTooltip title={buildChipTooltip(item)}>
      <Box
        className={`spv2-demand-chip is-${state}${compact ? " is-compact" : ""}${
          isDraggable ? " is-draggable" : ""
        }${dragging ? " is-dragging" : ""}`}
        style={{ "--spv2-demand-color": chipColor }}
        draggable={isDraggable}
        aria-label={buildChipTooltip(item)}
        onDragStart={isDraggable ? handleDragStart : undefined}
        onDragEnd={isDraggable ? handleDragEnd : undefined}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Box component="span" className="spv2-demand-chip-copy">
          <Box component="span" className="spv2-demand-chip-title">
            {chipTitleLabel(item)}
          </Box>
          {!compact ? (
            <Box component="span" className="spv2-demand-chip-time">
              {item.timeLabel}
            </Box>
          ) : null}
        </Box>
        <Box component="span" className="spv2-demand-chip-count">
          {item.plannedWorkers}/{item.requiredWorkers}
        </Box>
      </Box>
    </ShiftplanTooltip>
  );
}

/*
  Der Pill zeigt nur Marker + Zahl: eine Tagesspalte ist im Monatsraster nur
  65px breit, "+5 gedeckt" wird dort abgeschnitten. Die Wortform steht im
  Tooltip und im aria-label - dieselbe Sprache wie die Tages-Badges.
*/
function CoveredPill({ count, isClear, expanded, onToggle }) {
  const title = expanded
    ? "Gedeckte Bedarfe ausblenden"
    : `${formatCoveredLabel(count)} – zum Anzeigen klicken`;

  return (
    <ShiftplanTooltip title={title}>
      <button
        type="button"
        className={`spv2-demand-covered-pill${isClear ? " is-clear" : ""}${expanded ? " is-expanded" : ""}`}
        aria-expanded={expanded}
        aria-label={title}
        onClick={onToggle}
        onMouseDown={(event) => event.stopPropagation()}
      >
        <Box component="span" className="spv2-demand-covered-marker" aria-hidden="true">
          {expanded ? "–" : isClear ? <IconCheck fontSize="inherit" /> : "+"}
        </Box>
        <Box component="span" className="spv2-demand-covered-label">
          {count}
        </Box>
      </button>
    </ShiftplanTooltip>
  );
}

export default function DemandRow({
  days,
  demandByDay = {},
  expanded = false,
  compact = false,
  totalMissing = 0,
  totalOver = 0,
  canDrag = false,
  draggedDemandId = "",
  draggedShiftDateKey = "",
  onToggleExpanded,
  onDemandDragStart,
  onDemandDragEnd,
  onShiftDropOnDemand,
}) {
  const handleDragStart = (item) => {
    onDemandDragStart?.(buildDemandDragEntry(item));
  };

  // Zeilenweite Entscheidung: entweder alle Spalten bündeln oder keine -
  // gemischte Regeln in einer Zeile machen sie unlesbar.
  const maxItemsPerDay = days.reduce(
    (max, day) => Math.max(max, (demandByDay[day.key]?.items || []).length),
    0
  );
  const bundleCovered = expanded && maxItemsPerDay > COVERED_FILTER_MIN_ITEMS;

  return (
    <>
      <Box
        className={`spv2-demand-rowhead${expanded ? " is-expanded" : ""}`}
        role="rowheader"
      >
        <button
          type="button"
          className="spv2-demand-toggle"
          aria-expanded={expanded}
          aria-label={expanded ? "Bedarfsdetails einklappen" : "Bedarfsdetails ausklappen"}
          onClick={() => onToggleExpanded?.(!expanded)}
        >
          <Box component="span" className="spv2-demand-toggle-icon" aria-hidden="true">
            <GroupsOutlinedIcon fontSize="small" />
          </Box>
          <Box component="span" className="spv2-demand-toggle-copy">
            <Typography component="span" className="spv2-demand-toggle-title">
              Bedarf
            </Typography>
            <Typography
              component="span"
              className={`spv2-demand-toggle-meta${totalMissing > 0 ? " has-missing" : ""}${
                totalMissing === 0 && totalOver > 0 ? " has-over" : ""
              }`}
            >
              {formatRowMeta(totalMissing, totalOver)}
            </Typography>
          </Box>
          <IconChevronDown
            className={`spv2-demand-toggle-chevron${expanded ? " is-expanded" : ""}`}
            fontSize="small"
          />
        </button>
      </Box>

      {days.map((day) => (
        <DemandDayCell
          key={`demand-${day.key}`}
          day={day}
          dayDemand={demandByDay[day.key] || EMPTY_DAY}
          expanded={expanded}
          compact={compact}
          bundleCovered={bundleCovered}
          canDrag={canDrag}
          draggedDemandId={draggedDemandId}
          isRemoveCandidate={Boolean(draggedShiftDateKey) && draggedShiftDateKey === day.key}
          onDemandDragStart={handleDragStart}
          onDemandDragEnd={onDemandDragEnd}
          onShiftDropOnDemand={onShiftDropOnDemand}
        />
      ))}
    </>
  );
}

function DemandDayCell({
  day,
  dayDemand,
  expanded,
  compact,
  bundleCovered,
  canDrag,
  draggedDemandId,
  isRemoveCandidate,
  onDemandDragStart,
  onDemandDragEnd,
  onShiftDropOnDemand,
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [showCovered, setShowCovered] = useState(false);
  // Die rote Entfernen-Zone erscheint erst, wenn die gezogene Schicht
  // tatsächlich über der Bedarfszelle schwebt - nicht schon beim Drag-Start.
  const showRemoveZone = isRemoveCandidate && isDragOver;

  useEffect(() => {
    if (!isRemoveCandidate) setIsDragOver(false);
  }, [isRemoveCandidate]);

  useEffect(() => {
    // Zusammenklappen der Zeile setzt auch die einzelnen Tage zurück.
    if (!expanded || !bundleCovered) setShowCovered(false);
  }, [expanded, bundleCovered]);

  const handleShiftDragOver = (event) => {
    event.preventDefault();
    if (event.dataTransfer) event.dataTransfer.dropEffect = "move";
    setIsDragOver(true);
  };
  const handleShiftDragLeave = (event) => {
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsDragOver(false);
  };
  const handleShiftDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setIsDragOver(false);
    onShiftDropOnDemand?.();
  };

  const coveredCount = bundleCovered
    ? dayDemand.items.filter((item) => !needsAttention(item)).length
    : 0;
  const visibleItems =
    bundleCovered && !showCovered ? dayDemand.items.filter(needsAttention) : dayDemand.items;
  const hasCoveredPill = bundleCovered && coveredCount > 0;

  return (
    <Box
      className={`spv2-demand-cell${day.isWeekend ? " is-weekend" : ""}${expanded ? " is-expanded" : ""}${
        showRemoveZone ? " is-remove-target" : ""
      }`}
      role="gridcell"
      aria-label={`Bedarf ${day.key}`}
      onDragEnter={isRemoveCandidate ? handleShiftDragOver : undefined}
      onDragOver={isRemoveCandidate ? handleShiftDragOver : undefined}
      onDragLeave={isRemoveCandidate ? handleShiftDragLeave : undefined}
      onDrop={isRemoveCandidate ? handleShiftDrop : undefined}
    >
      {showRemoveZone ? (
        <Box component="span" className="spv2-demand-remove-hint">
          Entfernen
        </Box>
      ) : expanded ? (
        dayDemand.items.length ? (
          <Box className="spv2-demand-day">
            {visibleItems.length ? (
              <Box className="spv2-demand-chip-stack">
                {visibleItems.map((item) => (
                  <DemandChip
                    key={item.id}
                    item={item}
                    compact={compact}
                    canDrag={canDrag}
                    dragging={Boolean(draggedDemandId) && String(item.id) === String(draggedDemandId)}
                    onDragStart={onDemandDragStart}
                    onDragEnd={onDemandDragEnd}
                  />
                ))}
              </Box>
            ) : null}
            {hasCoveredPill ? (
              <CoveredPill
                count={coveredCount}
                isClear={coveredCount === dayDemand.items.length}
                expanded={showCovered}
                onToggle={() => setShowCovered((previous) => !previous)}
              />
            ) : null}
          </Box>
        ) : (
          <Box component="span" className="spv2-demand-empty" aria-hidden="true">
            –
          </Box>
        )
      ) : (
        <DaySummaryBadge day={dayDemand} />
      )}
    </Box>
  );
}
